/**
 * Kontakt auswählen — und notfalls gleich anlegen.
 *
 * Der Grund für diesen Baustein ist ein Ablauf, der vorher in eine
 * Sackgasse führte: Man tippt eine Rechnung, kommt zum Empfänger und
 * merkt, dass der Kunde noch nicht erfasst ist. Bisher hieß das: Maske
 * verlassen, Kontakt anlegen, zurückkommen — und alles Getippte war weg,
 * weil der Entwurf noch nicht gespeichert war.
 *
 * Der Dialog legt den Kontakt an, ohne die Maske zu verlassen, und wählt
 * ihn danach aus. Bewusst mit wenigen Feldern: Was hier steht, muss beim
 * Erfassen eines Belegs im Vorbeigehen auszufüllen sein. Alles Weitere —
 * IBAN, Standardkategorie, Zahlungsziel, Notiz — steht unter Kontakte und
 * wird dort nachgetragen.
 *
 * Eine Ausnahme davon ist die Anschrift: Sie ist bei einer Rechnung
 * Pflicht nach § 14 Abs. 4 UStG. Deshalb steht sie hier, wenn der Dialog
 * für eine Rechnung geöffnet wird, und darf beim Beleg fehlen.
 */
import { useState, type ReactNode } from 'react';
import { Dialog } from './Dialog';
import { Textfeld } from './Feld';
import { Fehlermeldung } from './Meldung';
import { useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import type { Kontakt, KontaktTyp } from '../lib/typen';

export function KontaktWahl({
  beschriftung,
  wert,
  setzeWert,
  kontakte,
  typ,
  mitAnschrift,
  pflicht,
  hinweis,
  leerText,
  neuGeladen,
}: {
  beschriftung: string;
  wert: string;
  setzeWert: (id: string) => void;
  kontakte: Kontakt[];
  /** Womit der neue Kontakt vorbelegt wird. */
  typ: KontaktTyp;
  /** Anschrift als Pflichtangabe zeigen — bei Rechnungen und Angeboten. */
  mitAnschrift?: boolean;
  pflicht?: boolean;
  hinweis?: string;
  leerText?: string;
  /** Wird nach dem Anlegen gerufen, damit die Liste den Neuzugang kennt. */
  neuGeladen: () => void;
}): ReactNode {
  const [offen, setzeOffen] = useState(false);
  const kennung = `kontakt-${beschriftung.toLowerCase().replace(/[^a-z]/g, '')}`;

  return (
    <>
      <div className="feld">
        <label htmlFor={kennung}>
          {beschriftung}
          {pflicht ? <span className="pflicht"> *</span> : null}
        </label>
        <div className="feld-mit-knopf">
          <select id={kennung} value={wert} onChange={(e) => setzeWert(e.target.value)}>
            <option value="">{leerText ?? '— bitte wählen —'}</option>
            {kontakte.map((k) => (
              <option key={k.id} value={k.id}>
                {k.ort ? `${k.name}, ${k.ort}` : k.name}
              </option>
            ))}
          </select>
          <button type="button" className="knopf schlicht" onClick={() => setzeOffen(true)}>
            + Neu
          </button>
        </div>
        {hinweis ? <span className="hinweis">{hinweis}</span> : null}
      </div>

      {offen ? (
        <NeuerKontakt
          typ={typ}
          mitAnschrift={mitAnschrift ?? false}
          schliessen={() => setzeOffen(false)}
          angelegt={(k) => {
            setzeWert(k.id);
            neuGeladen();
            setzeOffen(false);
          }}
        />
      ) : null}
    </>
  );
}

function NeuerKontakt({
  typ,
  mitAnschrift,
  schliessen,
  angelegt,
}: {
  typ: KontaktTyp;
  mitAnschrift: boolean;
  schliessen: () => void;
  angelegt: (kontakt: Kontakt) => void;
}): ReactNode {
  const vorgang = useVorgang();
  const [name, setzeName] = useState('');
  const [inhaber, setzeInhaber] = useState('');
  const [strasse, setzeStrasse] = useState('');
  const [plz, setzePlz] = useState('');
  const [ort, setzeOrt] = useState('');
  const [email, setzeEmail] = useState('');

  const fertig = name.trim() !== '' && (!mitAnschrift || (strasse.trim() !== '' && ort.trim() !== ''));

  async function anlegen(): Promise<void> {
    const neu = await vorgang.fuehreAus(() =>
      api.senden<Kontakt>('/api/kontakte', {
        typ,
        name: name.trim(),
        inhaber: inhaber.trim() || null,
        strasse: strasse.trim() || null,
        plz: plz.trim() || null,
        ort: ort.trim() || null,
        email: email.trim() || null,
      }),
    );
    if (neu !== undefined) angelegt(neu);
  }

  return (
    <Dialog
      titel={typ === 'lieferant' ? 'Lieferant anlegen' : 'Kunde anlegen'}
      schliessen={schliessen}
      fuss={
        <>
          <button type="button" className="knopf" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf haupt"
            disabled={!fertig || vorgang.laeuft}
            onClick={() => void anlegen()}
          >
            Anlegen und übernehmen
          </button>
        </>
      }
    >
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />

        <Textfeld
          beschriftung="Name"
          wert={name}
          setzeWert={setzeName}
          pflicht
          breit
          maxLaenge={200}
          platzhalter="Firma oder Person"
        />

        <Textfeld
          beschriftung="Inhaber oder Ansprechpartner"
          wert={inhaber}
          setzeWert={setzeInhaber}
          breit
          maxLaenge={200}
          hinweis="Steht nicht automatisch auf der Rechnung — wählbar als Empfängername."
        />

        {mitAnschrift ? (
          <>
            <Textfeld
              beschriftung="Straße und Hausnummer"
              wert={strasse}
              setzeWert={setzeStrasse}
              pflicht
              breit
              maxLaenge={200}
            />
            <div className="formularraster">
              <Textfeld beschriftung="PLZ" wert={plz} setzeWert={setzePlz} maxLaenge={10} />
              <Textfeld beschriftung="Ort" wert={ort} setzeWert={setzeOrt} pflicht maxLaenge={120} />
            </div>
          </>
        ) : (
          <div className="formularraster">
            <Textfeld beschriftung="PLZ" wert={plz} setzeWert={setzePlz} maxLaenge={10} />
            <Textfeld beschriftung="Ort" wert={ort} setzeWert={setzeOrt} maxLaenge={120} />
          </div>
        )}

        <Textfeld
          beschriftung="E-Mail"
          art="email"
          wert={email}
          setzeWert={setzeEmail}
          breit
          maxLaenge={200}
        />

        <p className="hinweis">
          IBAN, Zahlungsziel und Standardkategorie lassen sich später unter
          Kontakte nachtragen. Hier steht nur, was zum Weitertippen nötig ist.
        </p>
      </div>
    </Dialog>
  );
}
